import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FirebaseError } from 'firebase/app';
import { cn } from '../lib/utils';
import KodaCareLogoFull from '../components/Custom Icons/KodaCareLogoFull';
import { Eye, EyeOff, Check, X } from 'lucide-react';
import { useLanguage } from '../functions/useLanguage';
import { type TranslationKey } from '../functions/i18n';
import AppButton from '../components/AppButton';
import { getCheckoutUrl, getStarterCheckoutUrl, type StripePlanId, type StripeBillingCycle } from '../lib/stripeBilling';

interface PasswordRule {
  key: TranslationKey;
  test: (p: string) => boolean;
}

const PASSWORD_RULES: PasswordRule[] = [
  { key: 'register.rule.minLength', test: p => p.length >= 8 },
  { key: 'register.rule.uppercase', test: p => /[A-Z]/.test(p) },
  { key: 'register.rule.lowercase', test: p => /[a-z]/.test(p) },
  { key: 'register.rule.number',    test: p => /[0-9]/.test(p) },
  { key: 'register.rule.special',   test: p => /[^A-Za-z0-9]/.test(p) },
];

function strengthInfo(score: number): { key: TranslationKey; color: string } {
  if (score <= 1) return { key: 'register.strength.veryWeak', color: 'bg-error-red' };
  if (score === 2) return { key: 'register.strength.weak',    color: 'bg-accent-orange' };
  if (score === 3) return { key: 'register.strength.fair',    color: 'bg-respiratory-blue' };
  if (score === 4) return { key: 'register.strength.strong',  color: 'bg-positive-green' };
  return             { key: 'register.strength.veryStrong',   color: 'bg-positive-green' };
}

export default function Register() {
  const { register, loginWithGoogle } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get('continueUrl');
  const campaign = searchParams.get('campaign');
  const planParam = searchParams.get('plan');
  const cycleParam = searchParams.get('cycle');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  const passedRules = PASSWORD_RULES.filter(r => r.test(password));
  const score = passedRules.length;
  const strength = strengthInfo(score);
  const allRulesMet = score === PASSWORD_RULES.length;

  const firebaseErrorMessage = (code: string): string => {
    switch (code) {
      case 'auth/email-already-in-use':    return t('register.error.emailInUse');
      case 'auth/invalid-email':           return t('register.error.invalidEmail');
      case 'auth/weak-password':           return t('register.error.weakPassword');
      case 'auth/network-request-failed':  return t('register.error.network');
      default:                             return t('register.error.generic');
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!allRulesMet) {
      setError(t('register.error.passwordRequirements'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('register.error.passwordsMustMatch'));
      return;
    }
    setIsLoading(true);
    try {
      const uid = await register(email, password, fullName);
      const postCheckoutUrl = continueUrl
        ? `${window.location.origin}/verify-email?continueUrl=${encodeURIComponent(continueUrl)}`
        : `${window.location.origin}/verify-email`;
      const selectedPlan = planParam === 'vital' || planParam === 'lifetime' ? planParam as Exclude<StripePlanId, 'starter'> : null;
      const selectedCycle = cycleParam === 'yearly' ? 'yearly' : 'monthly';
      const checkoutUrl = selectedPlan
        ? getCheckoutUrl(selectedPlan, selectedCycle as StripeBillingCycle, { email, uid, redirectUrl: postCheckoutUrl })
        : getStarterCheckoutUrl({ email, uid, campaign, redirectUrl: postCheckoutUrl });

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        navigate(continueUrl ? `/verify-email?continueUrl=${encodeURIComponent(continueUrl)}` : '/verify-email');
      }
    } catch (err) {
      console.error('[Register] Registration error:', err);
      if (err instanceof FirebaseError) {
        setError(firebaseErrorMessage(err.code));
      } else {
        setError(t('register.error.generic'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setIsLoading(true);
    try {
      await loginWithGoogle();
      navigate(continueUrl ? decodeURIComponent(continueUrl) : '/splash');
    } catch (err) {
      if (err instanceof FirebaseError && err.code !== 'auth/popup-closed-by-user') {
        setError(t('register.error.googleFailed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn("min-h-screen flex flex-col items-center justify-center p-4", "bg-background-light")}>
      <div className={cn("w-full max-w-md rounded-3xl overflow-hidden", "bg-background-light")}>
        {/* Header */}
        <div className={cn("p-8 text-center", "bg-background-light")}>
          <div className="w-full h-auto flex items-center justify-center mx-auto mb-0">
            <KodaCareLogoFull className="w-full h-auto text-primary-brown" />
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          <h2 className={cn("text-xl font-bold mb-6", "text-primary-brown")}>{t('register.title')}</h2>

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-error-red\/5 border border-error-red\/20 text-error-red text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleRegister} className="space-y-4">
            {/* Full Name */}
            <div>
              <label className={cn("block text-sm font-medium mb-1", "text-text-secondary")}>{t('register.fullName')}</label>
              <input
                type="text"
                required
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                className={cn("w-full px-3 py-2.5 rounded-xl border border-slate-200 text-text-primary font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40")}
                placeholder="Your Full Name"
              />
            </div>

            {/* Email */}
            <div>
              <label className={cn("block text-sm font-medium mb-1", "text-text-secondary")}>{t('register.email')}</label>
              <input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                className={cn("w-full px-3 py-2.5 rounded-xl border border-slate-200 text-text-primary font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40")}
                placeholder="name@example.com"
              />
            </div>

            {/* Password */}
            <div>
              <label className={cn("block text-sm font-medium mb-1", "text-text-secondary")}>{t('register.password')}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className={cn("w-full px-3 py-2.5 pr-11 rounded-xl border border-slate-200 text-text-primary font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40")}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className={cn("absolute right-3 top-1/2 -translate-y-1/2", "text-gray-400 hover:text-text-secondary")}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {/* Strength bar */}
              {password.length > 0 && (
                <div className="mt-2 space-y-2">
                  <div className="flex gap-1 h-1.5">
                    {[1, 2, 3, 4, 5].map(i => (
                      <div
                        key={i}
                        className={cn("flex-1 rounded-full transition-colors", i <= score ? strength.color : "bg-gray-200")}
                      />
                    ))}
                  </div>
                  <p className={cn("text-xs font-medium", "text-text-secondary")}>
                    {t('register.passwordStrength')} <span className="font-semibold">{t(strength.key)}</span>
                  </p>
                  <ul className="space-y-1">
                    {PASSWORD_RULES.map(rule => {
                      const passed = rule.test(password);
                      return (
                        <li key={rule.key} className={cn("flex items-center gap-2 text-xs", passed ? "text-green-600" : "text-text-secondary")}>
                          {passed
                            ? <Check size={13} className="flex-shrink-0 text-green-500" />
                            : <X size={13} className="flex-shrink-0 text-gray-400" />}
                          {t(rule.key)}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className={cn("block text-sm font-medium mb-1", "text-text-secondary")}>{t('register.confirmPassword')}</label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  required
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className={cn(
                    "w-full px-3 py-2.5 pr-11 rounded-xl border border-slate-200 text-text-primary font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40",
                    confirmPassword.length > 0 && confirmPassword !== password
                      ? "border-error-red\/40 focus:ring-error-red\/40"
                      : ""
                  )}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(v => !v)}
                  className={cn("absolute right-3 top-1/2 -translate-y-1/2", "text-gray-400 hover:text-text-secondary")}
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {confirmPassword.length > 0 && confirmPassword !== password && (
                <p className="text-xs text-error-red mt-1">{t('register.error.passwordsMustMatch')}</p>
              )}
            </div>

            {/* Disclaimer checkbox */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="disclaimer"
                checked={disclaimerAccepted}
                required
                onChange={e => setDisclaimerAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 flex-shrink-0 cursor-pointer accent-primary-brown"
              />
              <label htmlFor="disclaimer" className="text-xs text-text-secondary leading-snug cursor-pointer">
                {t('register.disclaimerPrefix')}{' '}
                <a href="/website/terms#medical-disclaimer" target="_blank" rel="noopener noreferrer" className="text-primary-brown underline">{t('register.termsOfService')}</a>
                {' '}{t('register.and')}{' '}
                <a href="/website/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-brown underline">{t('register.privacyPolicy')}</a>.
              </label>
            </div>

            {/* Submit */}
            <AppButton
              type="submit"
              disabled={isLoading}
              className="p-4 mt-2"
            >
              {isLoading ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                t('register.createAccount')
              )}
            </AppButton>

            {/* Divider */}
            <div className="flex items-center gap-3 my-1">
              <div className={cn("flex-1 h-px", "bg-gray-200")} />
              <span className={cn("text-xs font-medium", "text-gray-400")}>{t('login.or')}</span>
              <div className={cn("flex-1 h-px", "bg-gray-200")} />
            </div>

            {/* Google */}
            <AppButton
              type="button"
              variant="secondary"
              onClick={handleGoogle}
              disabled={isLoading}
              className="p-3 mt-4 font-medium gap-3"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              {t('login.continueWithGoogle')}
            </AppButton>

            {/* Login link */}
            <p className={cn("text-center text-sm mt-2", "text-text-secondary")}>
              {t('register.haveAccount')}{' '}
              <Link to={continueUrl ? `/login?continueUrl=${encodeURIComponent(continueUrl)}` : "/login"} className="font-semibold text-primary-brown hover:underline">
                {t('register.signIn')}
              </Link>
            </p>

          </form>
        </div>
      </div>
    </div>
  );
}
